import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { ButtonSplitGroup } from '@components/Buttons';
import { didGroupsApi } from '@reducers/capacity';
import { countryApi } from '@reducers/geo';
import { skipToken } from '@reduxjs/toolkit/query';

const AutoSubmitFilter = ({ setCountry }) => {
  const formik = useFormikContext();

  useEffect(() => {
    formik.setFieldValue('plan', '');
    formik.setFieldValue('type', '');
    setCountry(formik.values.country);
  }, [formik.values.country]);

  useEffect(() => {
    if (!formik.values.plan || !formik.values.country) {
      formik.setTouched({}, false);
    }
  }, [formik.values.plan]);
};

export const DidGroupsCreateForm = () => {
  const [country, setCountry] = useState('');

  const countries = countryApi.useGetForSelectForDidGroupsQuery();
  const plan = didGroupsApi.useGetPlansForSelectQuery(
    country ? { id: country } : skipToken
  );

  const [createQuery, createResult] = didGroupsApi.useCreateDidGroupMutation();
  const navigate = useNavigate();

  const schema = Yup.object({
    country: Yup.string().required(),
    plan: Yup.string().required(),
    type: Yup.string().required(),
    name: Yup.string().required(),
  });
  const initialValues = {
    country,
    plan: '',
    type: '',
    name: '',
  };

  const onSubmit = async (values) => {
    createQuery({ data: values })
      .unwrap()
      .then(() => {
        navigate('/capacity/did-groups/existing');
      });
  };
  const handleCancelButton = () => {
    navigate(-1);
  };
  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        setFieldValue,
      }) => (
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={12}>
              <Form.Group
                as={Row}
                controlId="formGroupCountry"
                className="mb-2"
              >
                <Col md={3}>
                  <Form.Label className="fw-bold">Country</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Select
                    className="mb-3"
                    name="country"
                    size="sm"
                    value={values.country}
                    isValid={touched.country && !errors.country}
                    isInvalid={touched.country && errors.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="">Select country</option>
                    {!countries.isLoading &&
                    !countries.isError &&
                    countries.data.length > 0 ? (
                      <>
                        {countries.data.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.name}
                          </option>
                        ))}
                      </>
                    ) : null}
                  </Form.Select>
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="formGroupPlan" className="mb-2">
                <Col md={3}>
                  <Form.Label className="fw-bold">Plan</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Select
                    className="mb-3"
                    name="plan"
                    size="sm"
                    value={`${values.plan}-${values.type}`}
                    disabled={
                      plan.isLoading || plan.isFetching || !values.country
                    }
                    isValid={touched.plan && !errors.plan}
                    isInvalid={touched.plan && errors.plan}
                    onChange={(e) => {
                      const { dataset } =
                        e.target.options[e.target.selectedIndex];
                      handleChange(
                        e.target.name,
                        `${e.target.value}-${dataset.plantype}`,
                        dataset.plantype
                      );
                      setFieldValue('plan', dataset.planid || '');
                      setFieldValue('type', dataset.plantype || '');
                    }}
                    onBlur={handleBlur}
                  >
                    {plan.isLoading || plan.isFetching ? (
                      <option>Loading...</option>
                    ) : null}
                    {!plan.isLoading && !plan.isFetching ? (
                      <option value="">
                        {values.country
                          ? 'Select plan'
                          : 'Select country to see plans'}
                      </option>
                    ) : null}

                    {!plan.isUninitialized &&
                    !plan.isLoading &&
                    !plan.isFetching &&
                    !plan.isError &&
                    plan.data.length > 0
                      ? plan.data.map(({ value, name, plan_type: type }) => (
                          <option
                            key={`${value}-${type}-${name}`}
                            value={`${value}-${type}`}
                            data-plantype={type}
                            data-planid={value}
                          >
                            {name}
                          </option>
                        ))
                      : null}
                  </Form.Select>
                </Col>
              </Form.Group>
              <Form.Group as={Row} controlId="formGroupName" className="mb-2">
                <Col md={3}>
                  <Form.Label className="fw-bold">New DID-Group</Form.Label>
                </Col>
                <Col md={9}>
                  <Form.Control
                    name="name"
                    type="input"
                    size="sm"
                    value={values.name}
                    isValid={touched.name && !errors.name}
                    isInvalid={touched.name && errors.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Name"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col className="text-start mt-3" md={12}>
              <ButtonSplitGroup>
                <Button
                  variant="didxl"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={createResult.isLoading}
                >
                  Create DID-Group
                </Button>
                <Button
                  variant="didxl-danger"
                  size="sm"
                  onClick={handleCancelButton}
                >
                  Cancel
                </Button>
              </ButtonSplitGroup>
            </Col>
          </Row>
          <AutoSubmitFilter setCountry={setCountry} />
        </Form>
      )}
    </Formik>
  );
};
